import { useEffect, useState, useCallback } from "react";
import { serviceLabel } from "@/lib/serviceLabel";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Send, Check, XCircle, ExternalLink, Tag, CreditCard, Receipt, Link2, MessageSquare, Camera, SplitSquareHorizontal, AlertTriangle } from "lucide-react";
import { adminClient } from "./api";
import InvoiceBuilder from "./InvoiceBuilder";
import { promoDiscount } from "@/components/site/PromoField";
import ImageUploader from "./ImageUploader";
import ClaimsBlock from "./ClaimsBlock";

function AfterPhotos({ booking, onChanged }) {
  const [photos, setPhotos] = useState(booking.after_photos || []);
  const [pending, setPending] = useState("");
  const [title, setTitle] = useState("");
  const [caption, setCaption] = useState("");
  const [busy, setBusy] = useState(false);
  const before = booking.quote?.photo_url;

  const addPhoto = async (url) => {
    if (!url) return;
    const next = [...photos, url];
    setPhotos(next); setPending("");
    try { await adminClient.put(`/admin/bookings/${booking.id}/after-photos`, { after_photos: next }); toast.success("After photo saved"); }
    catch { toast.error("Could not save photo"); }
  };
  const removePhoto = async (url) => {
    const next = photos.filter((p) => p !== url);
    setPhotos(next);
    try { await adminClient.put(`/admin/bookings/${booking.id}/after-photos`, { after_photos: next }); } catch {}
  };
  const makePair = async (afterUrl, publish) => {
    setBusy(true);
    try {
      await adminClient.post(`/admin/bookings/${booking.id}/before-after`, { after_url: afterUrl, title, caption, publish });
      toast.success(publish ? "Before/after pair is live on the homepage" : "Pair saved as draft — publish it under Before After");
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create pair"); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="after-photos-block">
      <div className="grid grid-cols-3 gap-2">
        {photos.map((url) => (
          <div key={url} className="relative group" data-testid="after-photo-thumb">
            <img src={url} alt="After" className="w-full h-20 object-cover rounded-md border border-zinc-200" />
            <button onClick={() => removePhoto(url)} className="absolute top-1 right-1 bg-white/90 rounded-full p-0.5 text-red-600 opacity-0 group-hover:opacity-100" title="Remove"><XCircle className="w-4 h-4" /></button>
            {before && !booking.before_after_id && (
              <button data-testid="after-photo-make-pair" disabled={busy} onClick={() => makePair(url, false)} className="absolute bottom-1 left-1 right-1 bg-[#FFDF00] text-[#0C2E2B] text-[9px] font-black rounded px-1 py-0.5 flex items-center justify-center gap-1"><SplitSquareHorizontal className="w-3 h-3" /> Use as after</button>
            )}
          </div>
        ))}
      </div>
      <ImageUploader value={pending} onChange={addPhoto} testId="after-photo-uploader" />
      {!before && <p className="text-[10px] text-zinc-500">No customer "before" photo on this booking, so a pair can't be built — after photos are still saved for your records.</p>}
      {before && !booking.before_after_id && photos.length > 0 && (
        <div className="grid grid-cols-2 gap-2">
          <Input data-testid="pair-title-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder={`Title (default: ${String(booking.service_type || "Job").replace(/^\w/, (c) => c.toUpperCase())} · ${booking.zip_code})`} className="text-xs" />
          <Input data-testid="pair-caption-input" value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="Caption (optional)" className="text-xs" />
        </div>
      )}
      {booking.before_after_id && <p className="text-[10px] text-emerald-700 font-bold" data-testid="pair-exists-note">Before/after pair created from this job — manage it under Before After.</p>}
    </div>
  );
}

const API = process.env.REACT_APP_BACKEND_URL;
const money = (v) => `$${Number(v || 0).toFixed(2)}`;

function Row({ label, value, testId }) {
  return (
    <div className="flex justify-between gap-3 text-xs py-1 border-b border-zinc-100">
      <span className="text-zinc-500">{label}</span>
      <span data-testid={testId} className="font-semibold text-[#0C2E2B] text-right">{value ?? "—"}</span>
    </div>
  );
}

function QuoteBlock({ quote, booking }) {
  if (!quote) return <p className="text-xs text-zinc-500">No AI quote attached (booked without a photo).</p>;
  return (
    <div className="space-y-2" data-testid="drawer-quote-block">
      {quote.photo_url && (
        <a href={`${API}${quote.photo_url}`} target="_blank" rel="noreferrer" data-testid="drawer-quote-photo-link">
          <img src={`${API}${quote.photo_url}`} alt="Customer photo" className="w-full max-h-56 object-cover rounded-md border-2 border-zinc-200" data-testid="drawer-quote-photo" />
        </a>
      )}
      <div className="flex items-baseline gap-2">
        <span className="font-display font-black text-2xl text-[#0C2E2B]" data-testid="drawer-ai-range">${Math.round(quote.price_low)}–${Math.round(quote.price_high)}</span>
        <span className="text-[10px] uppercase tracking-widest text-zinc-400">AI estimate · {quote.confidence} confidence</span>
      </div>
      {quote.description && <p className="text-xs text-zinc-600">{quote.description}</p>}
      {quote.items_detected?.length > 0 && (
        <ul className="text-xs text-zinc-700 list-disc pl-4 space-y-0.5">{quote.items_detected.slice(0, 8).map((i, n) => <li key={n}>{i}</li>)}</ul>
      )}
      {booking?.notes && <p className="text-xs text-zinc-500 italic">Customer notes: {booking.notes}</p>}
    </div>
  );
}

function StoreReminderButton({ booking, onSaved }) {
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await adminClient.post(`/admin/bookings/${booking.id}/store-reminder`);
      toast.success(`Store reminded${data.sms?.ok ? " · texted" : ""}${data.email?.status === "sent" ? " · emailed" : ""}`);
      onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not reach the store"); } finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-2 pt-1" data-testid="drawer-store-reminder-block">
      <Button size="sm" variant="outline" data-testid="drawer-store-reminder-btn" disabled={busy} onClick={send} className="h-7 text-[11px] font-bold">{busy ? "Sending…" : "Remind store to stage order"}</Button>
      {booking.store_reminder_sent_at && <span className="text-[11px] text-emerald-700" data-testid="drawer-store-reminder-sent">Reminded {new Date(booking.store_reminder_sent_at).toLocaleString()}</span>}
    </div>
  );
}

function OverageButton({ booking, onSaved }) {
  const [busy, setBusy] = useState(false);
  const [hours, setHours] = useState(1);
  const rate = booking.furniture_depot?.hourly_overage || 45;
  const add = async () => {
    setBusy(true);
    try {
      const { data } = await adminClient.post(`/admin/bookings/${booking.id}/overage`, { hours: Number(hours), send_email: true });
      toast.success(`Invoiced ${data.overage_hours}h overage · balance ${money(data.booking.money?.balance_due)}${data.email?.status === "sent" ? " · emailed" : ""}`);
      onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not add overage"); } finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-2 pt-1 border-t border-zinc-200" data-testid="drawer-overage-block">
      <select data-testid="drawer-overage-hours" value={hours} onChange={(e) => setHours(e.target.value)} className="border border-zinc-300 rounded px-1.5 py-1 text-xs bg-white">
        {[0.5, 1, 1.5, 2, 3].map((h) => <option key={h} value={h}>+{h} hr</option>)}
      </select>
      <Button size="sm" data-testid="drawer-overage-btn" disabled={busy} onClick={add} className="h-7 text-[11px] font-bold bg-[#0C2E2B] hover:bg-[#0a2523]">
        {busy ? "Invoicing…" : `Invoice overage · ${money(Number(hours) * rate)}`}
      </Button>
      {booking.overage_hours > 0 && <span className="text-[11px] text-zinc-500" data-testid="drawer-overage-total">{booking.overage_hours}h billed so far</span>}
    </div>
  );
}

function FirmPrice({ booking, onSaved }) {
  const [price, setPrice] = useState(booking.firm_price ?? "");
  const [note, setNote] = useState(booking.firm_price_note || "");
  const [busy, setBusy] = useState(false);
  const promoOff = booking.promo ? promoDiscount(booking.promo, Number(price || 0)) : 0;
  const submit = async (sendEmail) => {
    if (!price || Number(price) <= 0) return toast.error("Enter a firm price");
    setBusy(true);
    try {
      const { data } = await adminClient.put(`/admin/bookings/${booking.id}/price`, { firm_price: Number(price), note, send_email: sendEmail });
      toast.success(sendEmail ? (data.email?.status === "sent" ? `Firm quote emailed to ${booking.email}` : `Saved — email ${data.email?.status || "skipped"}`) : "Firm price saved");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save price"); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-2" data-testid="firm-price-form">
      <div className="grid grid-cols-[120px_1fr] gap-2">
        <div>
          <Label className="text-[10px] font-bold uppercase tracking-widest text-zinc-600">Firm price</Label>
          <Input data-testid="firm-price-input" type="number" min="1" step="1" value={price} onChange={(e) => setPrice(e.target.value)} className="mt-1 font-mono-data font-bold" placeholder="0" />
        </div>
        <div>
          <Label className="text-[10px] font-bold uppercase tracking-widest text-zinc-600">Note to customer</Label>
          <Input data-testid="firm-price-note" value={note} onChange={(e) => setNote(e.target.value)} className="mt-1" placeholder="Includes disposal of the mattress and two dressers" />
        </div>
      </div>
      <div className="flex gap-2">
        <Button data-testid="firm-price-save-btn" size="sm" variant="outline" disabled={busy} onClick={() => submit(false)} className="rounded-md text-xs">Save only</Button>
        <Button data-testid="firm-price-send-btn" size="sm" disabled={busy || !booking.email} onClick={() => submit(true)} className="btn-kinetic rounded-md text-xs"><Send className="w-3 h-3 mr-1" /> Save & email quote</Button>
      </div>
      {booking.promo && Number(price) > 0 && <p data-testid="firm-price-promo-hint" className="text-[11px] text-emerald-700 font-bold">Customer's code {booking.promo.code} takes −{money(promoOff)} off → {money(Math.max(0, Number(price) - promoOff))} due (applied automatically)</p>}
      {booking.firm_price_emailed_at && <p className="text-[10px] text-zinc-500">Last emailed {new Date(booking.firm_price_emailed_at).toLocaleString()}</p>}
    </div>
  );
}

function PaymentRequests({ kind, target, onChanged }) {
  const defaultAmount = kind === "booking" || kind === "order"
    ? (target.money?.balance_due ?? null) !== null && target.money.balance_due > 0
      ? target.money.balance_due
      : (target.payment_status !== "paid" ? (kind === "order" ? Number(target.total || 0) : (target.deposit_amount || 25)) : 0)
    : Number(target.item_price || 0) + Number(target.fulfillment === "delivery" ? target.delivery_fee || 0 : 0);
  const [amount, setAmount] = useState(defaultAmount ? defaultAmount.toFixed(2) : "");
  const [label, setLabel] = useState(kind === "booking" ? (target.money?.total != null ? "Job balance" : "Deposit") : kind === "order" ? (target.invoice ? "Order balance" : "Order payment") : `${target.item_title || "Item"}${target.fulfillment === "delivery" ? " + delivery" : ""}`);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const send = async () => {
    if (!amount || Number(amount) <= 0) return toast.error("Enter an amount");
    setBusy(true);
    try {
      const { data } = await adminClient.post("/admin/payment-requests", { target_kind: kind, target_id: target.id, amount: Number(amount), label, note, site_url: window.location.origin });
      toast.success(data.email?.status === "sent" ? `Payment request emailed to ${target.email}` : `Created — email ${data.email?.status || "skipped"}`);
      setNote(""); onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send request"); } finally { setBusy(false); }
  };
  const markPaid = async (pr) => {
    try { await adminClient.put(`/admin/payment-requests/${pr.id}/mark-paid`); toast.success("Marked paid — receipt emailed"); onChanged(); }
    catch { toast.error("Could not mark paid"); }
  };
  const cancel = async (pr) => {
    try { await adminClient.delete(`/admin/payment-requests/${pr.id}`); toast.success("Request cancelled"); onChanged(); }
    catch { toast.error("Could not cancel"); }
  };

  return (
    <div className="space-y-3" data-testid="payment-requests-block">
      <div className="grid grid-cols-[110px_1fr] gap-2">
        <div>
          <Label className="text-[10px] font-bold uppercase tracking-widest text-zinc-600">Amount</Label>
          <Input data-testid="pr-amount-input" type="number" min="1" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-1 font-mono-data font-bold" />
        </div>
        <div>
          <Label className="text-[10px] font-bold uppercase tracking-widest text-zinc-600">What it's for</Label>
          <Input data-testid="pr-label-input" value={label} onChange={(e) => setLabel(e.target.value)} className="mt-1" />
        </div>
      </div>
      <Textarea data-testid="pr-note-input" rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional note to the customer" />
      <Button data-testid="pr-send-btn" size="sm" disabled={busy || !target.email} onClick={send} className="btn-kinetic rounded-md text-xs w-full">
        <CreditCard className="w-3 h-3 mr-1" /> {busy ? "Sending…" : "Email payment request (Zelle + card link)"}
      </Button>
      {!target.email && <p className="text-[10px] text-red-600">No customer email on file — can't send.</p>}

      {target.payment_requests?.length > 0 && (
        <ul className="divide-y divide-zinc-100 border border-zinc-200 rounded-md" data-testid="pr-list">
          {target.payment_requests.map((pr) => (
            <li key={pr.id} data-testid={`pr-row-${pr.id}`} className="p-2.5 text-xs flex items-center gap-2">
              <div className="flex-1 min-w-0">
                <div className="font-bold text-[#0C2E2B]">{money(pr.amount)} · {pr.label}</div>
                <div className="text-[10px] text-zinc-500 font-mono-data">{pr.memo} · {new Date(pr.created_at).toLocaleDateString()}{pr.paid_via ? ` · paid via ${pr.paid_via}` : ""}</div>
              </div>
              <Badge data-testid={`pr-status-${pr.id}`} className={`text-[9px] ${pr.status === "paid" ? "bg-emerald-600" : pr.status === "cancelled" ? "bg-zinc-400" : "bg-amber-500"}`}>{pr.status}</Badge>
              {pr.checkout_url && pr.status === "sent" && (
                <a href={pr.checkout_url} target="_blank" rel="noreferrer" className="text-zinc-400 hover:text-[#0C2E2B]" title="Open card link"><ExternalLink className="w-3.5 h-3.5" /></a>
              )}
              {pr.status === "sent" && (
                <>
                  <button data-testid={`pr-markpaid-${pr.id}`} onClick={() => markPaid(pr)} className="text-emerald-600 hover:text-emerald-800" title="Mark paid (Zelle)"><Check className="w-4 h-4" /></button>
                  <button data-testid={`pr-cancel-${pr.id}`} onClick={() => cancel(pr)} className="text-zinc-400 hover:text-red-600" title="Cancel"><XCircle className="w-4 h-4" /></button>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function RecordDrawer({ kind, recordId, onClose, onChanged }) {
  const [target, setTarget] = useState(null);
  const path = { booking: "bookings", inquiry: "inquiries", order: "orders" }[kind];

  const load = useCallback(() => {
    if (!recordId) return;
    adminClient.get(`/admin/${path}/${recordId}`).then(({ data }) => setTarget(data)).catch(() => toast.error("Could not load record"));
  }, [path, recordId]);

  useEffect(() => { setTarget(null); load(); }, [load]);
  const refresh = () => { load(); onChanged?.(); };

  return (
    <Sheet open={!!recordId} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto" data-testid="record-drawer">
        <SheetHeader>
          <SheetTitle className="font-display text-[#0C2E2B]">
            {target ? (kind === "booking" ? `${target.ref_id} · ${serviceLabel(target.service_type).toUpperCase()}` : kind === "order" ? `${target.ref_id} · Merch order` : `Inquiry · ${target.item_title}`) : "Loading…"}
          </SheetTitle>
          <SheetDescription>{target ? `${target.name} · ${target.phone}${target.email ? ` · ${target.email}` : ""}` : ""}</SheetDescription>
        </SheetHeader>

        {target && (
          <div className="mt-5 space-y-7">
            <section>
              <h4 className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-2">Details</h4>
              {kind === "booking" ? (
                <>
                  <Row label="Status" value={String(target.status || "").replace(/_/g, " ")} testId="drawer-status" />
                  <Row label="When" value={`${target.preferred_date} · ${target.preferred_window}`} />
                  <Row label="Address" value={`${target.address}, ${target.zip_code}`} />
                  <Row label="Deposit" value={`${money(target.deposit_amount)} · ${target.payment_status || "pending"} (${target.payment_method})`} />
                  <Row label="Firm price" value={target.firm_price ? money(target.firm_price) : "not set"} testId="drawer-firm-price" />
                  {target.furniture_depot && (
                    <div data-testid="drawer-furniture-depot" className="rounded-md border border-zinc-200 bg-zinc-50 p-2 text-xs space-y-1">
                      <div className="font-bold text-[#0C2E2B]">Furniture Depot · {target.furniture_depot.store?.name}</div>
                      {target.furniture_depot.lines?.map((l, i) => <div key={i} className="flex justify-between gap-2"><span>{l.label}</span><span className="font-mono-data">{money(l.amount)}</span></div>)}
                      <div className="flex justify-between font-bold border-t border-zinc-200 pt-1"><span>Total (paid in full at booking)</span><span className="font-mono-data">{money(target.furniture_depot.total)}</span></div>
                      {target.furniture_depot.distance?.known && <div className="text-zinc-500">≈{target.furniture_depot.distance.miles} mi from store · {target.furniture_depot.overage_note}</div>}
                      {target.store_notified_at && <div className="text-emerald-700" data-testid="drawer-store-notified">Store notified · {target.store_notified_email}</div>}
                      {target.store_flag && !target.store_flag.cleared_at && (
                        <div data-testid="drawer-store-flag" className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-800 rounded px-2 py-1">
                          <span className="font-bold">NOT READY · {target.store_flag.reason_label}</span>{target.store_flag.note && <span>— {target.store_flag.note}</span>}
                          <span className="text-red-500 text-[10px]">{new Date(target.store_flag.at).toLocaleString()}</span>
                          <Button size="sm" variant="outline" data-testid="drawer-store-flag-clear" className="h-6 text-[10px] ml-auto" onClick={async () => { try { await adminClient.post(`/admin/bookings/${target.id}/store-flag/clear`); toast.success("Flag cleared"); refresh(); } catch { toast.error("Could not clear"); } }}>Clear</Button>
                        </div>
                      )}
                      <OverageButton booking={target} onSaved={refresh} />
                      <StoreReminderButton booking={target} onSaved={refresh} />
                    </div>
                  )}
                  {target.promo?.code && <Row label="Promo" value={`${target.promo.code} · ${target.promo.label || ""}${target.money?.promo_discount > 0 ? ` · −${money(target.money.promo_discount)} → ${money(target.money.total)} due` : " · applies to the final price"}`} testId="drawer-booking-promo" />}
                  {target.money?.total != null && <Row label="Balance due" value={money(target.money.balance_due)} testId="drawer-balance-due" />}
                  {target.amount_paid > 0 && <Row label="Paid via requests" value={money(target.amount_paid)} testId="drawer-amount-paid" />}
                  <div className="flex items-center justify-between pt-2 text-xs">
                    <span className="text-zinc-500 flex items-center gap-1"><Link2 className="w-3 h-3" /> Customer pay link</span>
                    <button
                      data-testid="drawer-copy-pay-link"
                      onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/pay/${target.ref_id}`); toast.success("Pay link copied"); }}
                      className="font-mono-data font-bold text-[#0C2E2B] underline"
                    >/pay/{target.ref_id}</button>
                  </div>
                  <Button
                    data-testid="drawer-text-pay-link-btn"
                    size="sm"
                    variant="outline"
                    disabled={!target.sms_consent || target.sms_opted_out}
                    title={!target.sms_consent ? "Customer didn't opt in to texts" : target.sms_opted_out ? "Customer replied STOP" : "Text the pay link"}
                    onClick={async () => {
                      try {
                        const { data } = await adminClient.post(`/admin/bookings/${target.id}/text-pay-link`, { site_url: window.location.origin });
                        toast.success(`Texted pay link to ${data.to}`);
                        refresh();
                      } catch (e) { toast.error(e?.response?.data?.detail || "Could not send text", { duration: 10000 }); }
                    }}
                    className="mt-2 w-full rounded-md text-xs h-8"
                  >
                    <MessageSquare className="w-3 h-3 mr-1" /> Text pay link to {target.phone}{target.pay_link_texted_at ? " (again)" : ""}
                  </Button>
                  {!target.sms_consent && <p className="text-[10px] text-zinc-400 mt-1">Texting disabled — customer didn't tick the SMS box. Email links still work.</p>}
                </>
              ) : kind === "order" ? (
                <>
                  <Row label="Status" value={String(target.status || "").replace(/_/g, " ")} testId="drawer-status" />
                  <Row label="Ship to" value={target.address} />
                  <Row label="Items" value={(target.items || []).map((i) => `${i.quantity}× ${i.title}${i.size ? ` (${i.size})` : ""}`).join(", ")} />
                  <Row label="Order total" value={`${money(target.total)} · ${target.payment_status || "pending"} (${target.payment_method})`} />
                  {target.promo?.code && <Row label="Promo" value={`${target.promo.code} · −${money(target.discount)}${target.promo.free_shipping ? " · free shipping" : ""}`} testId="drawer-order-promo" />}
                  {target.invoice && <Row label="Invoice total" value={money(target.invoice.total)} testId="drawer-firm-price" />}
                  {target.money && <Row label="Balance due" value={money(target.money.balance_due)} testId="drawer-balance-due" />}
                  <div className="flex items-center justify-between pt-2 text-xs">
                    <span className="text-zinc-500 flex items-center gap-1"><Link2 className="w-3 h-3" /> Customer pay link</span>
                    <button data-testid="drawer-copy-pay-link" onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/pay/${target.ref_id}`); toast.success("Pay link copied"); }} className="font-mono-data font-bold text-[#0C2E2B] underline">/pay/{target.ref_id}</button>
                  </div>
                </>
              ) : (
                <>
                  <Row label="Status" value={target.status || "new"} testId="drawer-status" />
                  <Row label="Item price" value={money(target.item_price)} />
                  <Row label="Fulfillment" value={target.fulfillment === "pickup" ? "Pickup (free)" : `Delivery · ${money(target.delivery_fee)}`} />
                  <Row label="Message" value={target.message || "—"} />
                  {target.amount_paid > 0 && <Row label="Paid" value={money(target.amount_paid)} testId="drawer-amount-paid" />}
                </>
              )}
            </section>

            {kind === "booking" && (
              <>
                <section>
                  <h4 className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-2">Customer photo & AI quote</h4>
                  <QuoteBlock quote={target.quote} booking={target} />
                </section>
                <section>
                  <h4 className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-1"><Tag className="w-3 h-3" /> Set firm price</h4>
                  <FirmPrice key={target.firm_price_set_at || "new"} booking={target} onSaved={refresh} />
                </section>
                <section>
                  <h4 className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-1"><Receipt className="w-3 h-3" /> Invoice {target.invoice ? `· v${target.invoice.version}` : ""}</h4>
                  <InvoiceBuilder key={target.invoice?.updated_at || "new"} booking={target} onSaved={refresh} />
                </section>
                <section>
                  <h4 className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-1"><Camera className="w-3 h-3" /> After photos & before/after pair</h4>
                  <AfterPhotos booking={target} onChanged={refresh} />
                </section>
                {(target.delivery_receipt || target.claims?.length > 0) && (
                  <section>
                    <h4 className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Delivery receipt & damage claims {target.claims?.length > 0 ? `· ${target.claims.length}` : ""}</h4>
                    <ClaimsBlock booking={target} onChanged={refresh} />
                  </section>
                )}
              </>
            )}

            {kind === "order" && (
              <section>
                <h4 className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-1"><Receipt className="w-3 h-3" /> Adjusted invoice {target.invoice ? `· v${target.invoice.version}` : ""}</h4>
                <InvoiceBuilder key={target.invoice?.updated_at || "new"} booking={target} endpoint={`/admin/orders/${target.id}/invoice`} onSaved={refresh} />
              </section>
            )}

            <section>
              <h4 className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-1"><CreditCard className="w-3 h-3" /> Request payment</h4>
              <PaymentRequests key={`${target.firm_price}-${target.payment_requests?.length}`} kind={kind} target={target} onChanged={refresh} />
            </section>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
